import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { de, feld } from "@/components/qm/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vertrieb-plus`;
const fehler = (e) => toast.error(e?.response?.data?.detail || "Fehler");
const eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const q = (v) => (v == null ? "–" : `${String(v).replace(".", ",")} %`);
const heute = new Date().toISOString().slice(0, 10);

const Kachel = ({ l, v, testid }) => (
  <div className="bg-white rounded-xl border border-[#F0E6DC] p-4" data-testid={testid}><p className="text-xs uppercase tracking-wider text-[#8E7B71]">{l}</p><p className="text-2xl font-semibold mt-1">{v}</p></div>
);

const MeinCockpit = () => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/mein-cockpit`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d?.ma) return null;
  return (
    <section className="space-y-3" data-testid="mein-vertriebscockpit">
      <h2 className="text-lg font-semibold">Mein Cockpit – {d.ma.name}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kachel l="Abschlüsse diesen Monat" v={d.abschluesse_monat} testid="cockpit-abschluesse" />
        <Kachel l="Umsatz diesen Monat" v={eur(d.umsatz_monat)} testid="cockpit-umsatz" />
        <Kachel l="Provision diesen Monat" v={eur(d.provision_monat)} testid="cockpit-provision" />
        <Kachel l="Abschlussquote im Jahr" v={q(d.quote_jahr)} testid="cockpit-quote" />
      </div>
      <div className="bg-white rounded-xl border border-[#F0E6DC] divide-y divide-[#F6E3DA]" data-testid="cockpit-wiedervorlagen">
        <p className="p-3 text-sm font-semibold">Wiedervorlagen fällig ({d.wiedervorlagen.length})</p>
        {d.wiedervorlagen.map((w) => (
          <a key={w.id} href={`/pflegeeinrichtungen/${w.id}`} className="p-2.5 flex gap-3 text-sm hover:bg-[#FBF8F5]">
            <span className="font-medium">{w.einrichtungsname}</span><span className="text-[#78716c]">{de(w.naechste_wiedervorlage)}</span><span className="text-xs text-[#8E7B71]">{w.wiedervorlage_grund}</span>
          </a>
        ))}
      </div>
    </section>
  );
};

const Quoten = () => {
  const [von, setVon] = useState(`${heute.slice(0, 4)}-01-01`);
  const [bis, setBis] = useState(heute);
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/quoten?von=${von}&bis=${bis}`).then((r) => setD(r.data)).catch(fehler); }, [von, bis]);
  const Tab = ({ kopf, zeilen, testid }) => (
    <table className="w-full text-sm bg-white rounded-xl border border-[#F0E6DC]" data-testid={testid}>
      <thead className="text-xs text-left text-[#8E7B71]"><tr>{kopf.map((k) => <th key={k} className="p-2">{k}</th>)}</tr></thead>
      <tbody className="divide-y divide-[#F6E3DA]">{zeilen.length ? zeilen.map((z, i) => <tr key={i}>{z.map((c, j) => <td key={j} className="p-2">{c}</td>)}</tr>)
        : <tr><td colSpan={kopf.length} className="p-3 text-[#8E7B71]">Keine Daten im Zeitraum.</td></tr>}</tbody>
    </table>
  );
  return (
    <section className="space-y-3" data-testid="abschlussquoten">
      <div className="flex flex-wrap items-center gap-2"><h2 className="text-lg font-semibold mr-2">Abschlussquoten</h2>
        <input type="date" className="rounded-md border border-[#E0D6CA] bg-white px-2.5 py-1.5 text-sm" value={von} onChange={(e) => setVon(e.target.value)} data-testid="quoten-von" />–
        <input type="date" className="rounded-md border border-[#E0D6CA] bg-white px-2.5 py-1.5 text-sm" value={bis} onChange={(e) => setBis(e.target.value)} data-testid="quoten-bis" /></div>
      {d && <div className="grid md:grid-cols-2 gap-4">
        <div><p className="text-xs text-[#8E7B71] mb-1">Je Person: kontaktierte Einrichtungen → Erstabschlüsse</p>
          <Tab testid="quoten-personen" kopf={["Person", "Kontaktiert", "Abschlüsse", "Quote"]} zeilen={d.personen.map((p) => [p.name, p.kontaktiert, p.abschluesse, q(p.quote)])} /></div>
        <div><p className="text-xs text-[#8E7B71] mb-1">Je Quelle: Interessenten → Teilnahmen</p>
          <Tab testid="quoten-quellen" kopf={["Quelle", "Leads", "Teilnahmen", "Quote"]} zeilen={d.quellen.map((x) => [x.quelle, x.leads, x.teilnahmen, q(x.quote)])} /></div>
      </div>}
    </section>
  );
};

const Regeln = ({ istGf }) => {
  const [liste, setListe] = useState([]);
  const [monat, setMonat] = useState(heute.slice(0, 7));
  const laden = useCallback(() => axios.get(`${API}/provisionsregeln`).then((r) => setListe(r.data)).catch(fehler), []);
  useEffect(() => { laden(); }, [laden]);
  const neu = (m) => {
    const gueltig_ab = window.prompt("Gültig ab (JJJJ-MM-TT):", heute); if (!gueltig_ab) return;
    const erstabschluss = window.prompt("Satz Erstabschluss in % (max. 20):", "20"); if (erstabschluss === null) return;
    const folgeauftrag = window.prompt("Satz Folgegeschäft in % (max. 20):", "10"); if (folgeauftrag === null) return;
    axios.post(`${API}/provisionsregeln/${m.id}`, { gueltig_ab, erstabschluss: erstabschluss.replace(",", "."), folgeauftrag: folgeauftrag.replace(",", ".") })
      .then(() => { toast.success("Regel gespeichert"); laden(); }).catch(fehler);
  };
  const pdf = (m) => axios.get(`${API}/provisionsabrechnung/pdf?ma_id=${m.id}&monat=${monat}`, { responseType: "blob" })
    .then((r) => window.open(URL.createObjectURL(r.data), "_blank")).catch(fehler);
  return (
    <section className="space-y-3" data-testid="provisionsregeln">
      <div className="flex items-center gap-2"><h2 className="text-lg font-semibold mr-2">Provisionsregeln &amp; Monatsabrechnung</h2>
        <input type="month" className="rounded-md border border-[#E0D6CA] bg-white px-2.5 py-1.5 text-sm" value={monat} onChange={(e) => setMonat(e.target.value)} data-testid="provision-monat" /></div>
      <div className="bg-white rounded-xl border border-[#F0E6DC] divide-y divide-[#F6E3DA]">
        {liste.map((m) => {
          const r = [...(m.provisionsregeln || [])].sort((a, b) => b.gueltig_ab.localeCompare(a.gueltig_ab));
          const aktuell = r.find((x) => x.gueltig_ab <= heute);
          const geplant = r.filter((x) => x.gueltig_ab > heute).pop();
          return (
            <div key={m.id} className="p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`regel-${m.id}`}>
              <span className="font-medium min-w-[160px]">{m.name}{m.aktiv === false && " (inaktiv)"}</span>
              <span className="text-[#78716c]" data-testid={`regel-aktuell-${m.id}`}>{aktuell ? `seit ${de(aktuell.gueltig_ab)}: Erstabschluss ${q(aktuell.erstabschluss)}, Folgegeschäft ${q(aktuell.folgeauftrag)}` : `${q(m.provisionssatz || m.provision_neukunde_prozent || 20)} für alles`}</span>
              {geplant && <span className="text-xs text-[#8A6116]" data-testid={`regel-geplant-${m.id}`}>geplant ab {de(geplant.gueltig_ab)}: {q(geplant.erstabschluss)} / {q(geplant.folgeauftrag)}</span>}
              <span className="ml-auto flex gap-2">
                {istGf && <Button size="sm" variant="outline" onClick={() => neu(m)} data-testid={`regel-neu-${m.id}`}>Neue Regel</Button>}
                <Button size="sm" variant="outline" onClick={() => pdf(m)} data-testid={`provision-pdf-${m.id}`}><FileText size={14} className="mr-1" />Abrechnung PDF</Button>
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default function VertriebAuswertung() {
  const { user } = useAuth();
  const buero = ["gf", "verwaltung"].includes(user?.role);
  return (
    <div className="p-6 lg:p-8 max-w-7xl space-y-8" data-testid="vertrieb-auswertung">
      <div><p className="text-[11px] uppercase tracking-[0.2em] text-[#9A3B2E] font-semibold">Vertrieb</p><h1 className="text-3xl font-semibold mt-1">Vertrieb &amp; Provisionen</h1></div>
      <MeinCockpit />
      {buero && <Quoten />}
      {buero && <Regeln istGf={user?.role === "gf"} />}
    </div>
  );
}
